'use client';

import './sheet.scss';

import { PropsWithChildren, forwardRef } from 'react';

import { cn } from '../../utils/helpers';
import {
  Modal,
  ModalClose,
  ModalCloseButton,
  ModalContent,
  ModalContentProps,
  ModalProps,
  ModalTitle,
  ModalTrigger,
  ModalTriggerProps,
} from '../modal/modal';

export type SheetProps = ModalProps;
export type SheetContentProps = ModalContentProps;

export const Sheet = Modal;

export const SheetHeader: React.FC<React.PropsWithChildren> = ({
  children,
}) => (
  <header className="flex flex-shrink-0 justify-between items-center w-full mx-auto p-comfy-sm md:px-comfy-xl lg:py-comfy-md lg:max-w-md lg:px-0">
    <ModalTitle className="heading-xs lg:heading-sm">{children}</ModalTitle>
    <ModalCloseButton
      name="Sheet"
      className="Sheet__close mr-snug-lg lg:mr-comfy-xs"
    />
  </header>
);

export const SheetBody: React.FC<React.PropsWithChildren> = ({ children }) => (
  <div className="Sheet__body flex-1 overflow-auto w-full pb-roomy-md">
    {/* Max-width container here, padding added here to allow divider lines to take up full width */}
    <div className="max-w-full lg:max-w-md mx-auto py-comfy-lg px-comfy-sm md:p-comfy-xl lg:px-0">
      {children}
    </div>
  </div>
);

export interface SheetBodyHeaderProps extends PropsWithChildren<
  React.HTMLAttributes<HTMLDivElement>
> {
  isSticky?: boolean;
}

/**
 * A sticky header component for SheetBody
 */
export const SheetBodyHeader: React.FC<SheetBodyHeaderProps> = ({
  isSticky = false,
  children,
  className = '',
  ...props
}) => {
  return (
    <div
      className={cn(
        `Sheet__body-header pt-comfy-xl -mt-comfy-xl top-0 ${className}`,
        {
          'Sheet__body-header--sticky sticky': isSticky,
        },
      )}
      {...props}
    >
      {children}
    </div>
  );
};

export const SheetFooter: React.FC<React.PropsWithChildren> = ({
  children,
}) => (
  <div className="Sheet__footer absolute w-full bottom-0 left-0 flex-shrink-0 px-comfy-sm py-comfy-xs">
    <div className="mx-auto md:px-comfy-xl w-full lg:max-w-md">{children}</div>
  </div>
);

// TODO: Add functionality for "isNarrow" prop (where max-width of content changes): essentially default to the wide version (768px) and have a isNarrow or something property which would drive the more narrow constraint (500px). Note that this does not affect the Sheet width, just the max width of the content inside the sheet.
export const SheetContent = forwardRef<HTMLDivElement, SheetContentProps>(
  ({ style, children, ...props }, ref) => {
    return (
      <ModalContent
        ref={ref}
        aria-describedby={undefined}
        className="Sheet__content w-full flex flex-col"
        style={{
          ...style,
        }}
        {...props}
      >
        {children}
      </ModalContent>
    );
  },
);
/**
 * Sheet trigger.
 */
export type SheetTriggerProps = ModalTriggerProps;
export const SheetTrigger = ModalTrigger;

/**
 * Sheet close.
 */
export const SheetClose = ModalClose;

export const SheetActions: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  return (
    <div className="flex items-center justify-center gap-comfy-xs md:gap-comfy-sm">
      {children}
    </div>
  );
};
